import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Chip,
  Divider,
  ListItemText,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  Avatar,
  Tooltip,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useDemoRole, DEMO_PERSONAS, PERSONA_TO_ROLE_KEY } from '../../contexts/DemoRoleContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLE_DEFINITIONS } from '../../services/roleService';
import { VIEW_AS_ROLE_ORDER, groupMembersByRole, filterMembers } from '../../services/viewAs';

/**
 * RoleSwitcher — "View as" (Beta-1 plan P4-03 / P4-04 / P4-08, Tim C0814).
 * One section per role in Tim's order — Administrator → Executive → Facility/Site/Plant Manager →
 * Department Head → Frontline Supervisor / Team Leader → Individual Contributor → Org IT →
 * PPI Tech Support (superadmins only). The role row itself is the generic lens (the role's default
 * view); the chevron expands the REAL members of that role, alphabetical by last name, with a count
 * badge; the search box filters across every section (200+ member orgs). Only members the viewer
 * may see appear (downward visibility); "View as yourself" returns to the viewer's own role.
 */
const ROLE_TO_PERSONA = Object.fromEntries(Object.entries(PERSONA_TO_ROLE_KEY).map(([p, r]) => [r, p]));

function initials(m) {
  const name = m.displayName || m.email || '';
  const parts = name.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  return (parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '');
}

/**
 * @param {boolean} asMenuItem  render as a row inside the avatar menu (small screens / entry page)
 */
function RoleSwitcher({ asMenuItem = false, compact = false, onNavigate } = {}) {
  const {
    persona,
    setPersonaId,
    visibleMembers,
    lensRoles,
    ownPersonaId,
    viewAsUid,
    setViewAs,
    viewingAs,
    canViewAs,
  } = useDemoRole();
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});
  const open = Boolean(anchorEl);

  const superadmin = lensRoles.length === VIEW_AS_ROLE_ORDER.length && DEMO_PERSONAS.some((p) => p.id === 'ppi') && ['alex.samaniego@ppiweb.com', 'tim.autrey@ppiweb.com'].includes((user?.email || '').toLowerCase());

  const filtered = useMemo(() => filterMembers(visibleMembers, query), [visibleMembers, query]);
  const groups = useMemo(() => groupMembersByRole(filtered, VIEW_AS_ROLE_ORDER.filter((r) => lensRoles.includes(r))), [filtered, lensRoles]);
  const searching = query.trim().length > 0;

  if (!canViewAs) return null;

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setQuery('');
    if (asMenuItem && onNavigate) onNavigate();
  };
  const handleSelectLens = (role) => {
    setPersonaId(ROLE_TO_PERSONA[role] || 'itm'); // also clears any view-as selection
    handleClose();
  };
  const handleSelectMember = (uid) => {
    setViewAs(uid);
    handleClose();
  };
  const handleSelf = () => {
    setPersonaId(ownPersonaId); // P4-08: back to the viewer's OWN role, not "admin"
    setViewAs(null);
    handleClose();
  };
  const toggle = (role) => setExpanded((e) => ({ ...e, [role]: !e[role] }));

  const nameOf = (m) => m.displayName || m.email || m.uid;
  const currentLabel = viewingAs ? nameOf(viewingAs) : persona.label;

  return (
    <>
      {compact && !asMenuItem ? (
        <Tooltip title={`View as: ${currentLabel}`}>
          <IconButton color="inherit" onClick={handleOpen} data-tour="view-as" aria-label={`View as: ${currentLabel}`} aria-haspopup="menu">
            {viewingAs ? <PersonIcon /> : <GroupIcon />}
          </IconButton>
        </Tooltip>
      ) : asMenuItem ? (
        <MenuItem onClick={handleOpen} data-tour="view-as" sx={{ borderRadius: 1 }}>
          <ListItemText
            primary={<Typography variant="body2" fontWeight={600}>View as</Typography>}
            secondary={<Typography variant="caption" color="text.secondary" noWrap>{currentLabel}</Typography>}
          />
          <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
        </MenuItem>
      ) : (
      <Button
        color="inherit"
        startIcon={viewingAs ? <PersonIcon /> : <GroupIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleOpen}
        data-tour="view-as"
        sx={{ textTransform: 'none', mr: 1, px: 1.5, borderRadius: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
      >
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, lineHeight: 1 }}>
            View as
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {currentLabel}
          </Typography>
        </Box>
      </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: { xs: 'calc(100vw - 16px)', sm: 380 }, mt: 0.5, maxHeight: { xs: '70vh', sm: 560 } } } }}
        MenuListProps={{ dense: true, 'aria-label': 'View as' }}
      >
        <Box sx={{ px: 2, pt: 1.25, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>View as</Typography>
            <Chip label="Downward only" size="small" color="warning" variant="outlined" sx={{ ml: 'auto', fontSize: '0.65rem', height: 20 }} />
          </Box>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="Search members (name, email, title)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            inputProps={{ 'aria-label': 'Search members' }}
          />
        </Box>
        <Divider />

        {groups.map(({ role, members }) => {
          const lensPersona = DEMO_PERSONAS.find((p) => p.id === ROLE_TO_PERSONA[role]);
          const isOpen = searching ? members.length > 0 : Boolean(expanded[role]);
          const lensSelected = !viewingAs && persona.id === ROLE_TO_PERSONA[role];
          return (
            <Box key={role}>
              <MenuItem selected={lensSelected} onClick={() => handleSelectLens(role)} sx={{ py: 0.75, pr: 1 }} aria-label={`${ROLE_DEFINITIONS[role]?.label || role} lens`}>
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={700}>{ROLE_DEFINITIONS[role]?.label || role}</Typography>}
                  secondary={<Typography variant="caption" color="text.secondary">{lensPersona?.description || ''}</Typography>}
                />
                <Chip label={members.length} size="small" sx={{ ml: 1, minWidth: 32, height: 20, fontSize: '0.7rem' }} aria-label={`${members.length} members`} />
                <IconButton
                  size="small"
                  aria-label={isOpen ? `Collapse ${ROLE_DEFINITIONS[role]?.label || role}` : `Expand ${ROLE_DEFINITIONS[role]?.label || role}`}
                  aria-expanded={isOpen}
                  disabled={members.length === 0}
                  onClick={(e) => { e.stopPropagation(); toggle(role); }}
                  sx={{ ml: 0.5, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              </MenuItem>
              <Collapse in={isOpen} unmountOnExit>
                {members.map((m) => (
                  <MenuItem key={m.uid} selected={m.uid === viewAsUid} onClick={() => handleSelectMember(m.uid)} sx={{ py: 0.5, pl: 3 }}>
                    <Avatar src={m.photoURL || undefined} sx={{ width: 26, height: 26, fontSize: '0.7rem', mr: 1.25, bgcolor: 'primary.light' }}>{initials(m)}</Avatar>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight={m.uid === viewAsUid ? 700 : 500} noWrap>{nameOf(m)}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{[m.title, m.email].filter(Boolean).join(' · ')}</Typography>}
                    />
                  </MenuItem>
                ))}
              </Collapse>
            </Box>
          );
        })}
        {searching && filtered.length === 0 && (
          <MenuItem disabled><Typography variant="body2" color="text.secondary">No members match “{query}”</Typography></MenuItem>
        )}
        {!searching && visibleMembers.length === 0 && (
          <MenuItem disabled sx={{ whiteSpace: 'normal' }}>
            <Typography variant="body2" color="text.secondary">No members below you yet — add people in Settings → Organization</Typography>
          </MenuItem>
        )}

        {superadmin && (
          <MenuItem selected={!viewingAs && persona.id === 'ppi'} onClick={() => { setPersonaId('ppi'); handleClose(); }} sx={{ py: 0.75 }}>
            <ListItemText
              primary={<Typography variant="body2" fontWeight={700}>REPSit.ai Tech Support</Typography>}
              secondary={<Typography variant="caption" color="text.secondary">REPSit.ai internal — tech fixes/adjustments</Typography>}
            />
          </MenuItem>
        )}

        <Divider />
        <MenuItem onClick={handleSelf} sx={{ py: 1 }}>
          <ListItemText primary={<Typography variant="body2" fontWeight={600}>View as yourself</Typography>} />
        </MenuItem>
      </Menu>
    </>
  );
}

export default RoleSwitcher;
